<!--
  SettlementList.vue
  Callbot monthly settlement - hospital usage list (/callbot/settlement), the first screen. Laid out like the approved
  2026-10-27 Main mockup.

  Request: ~/md/front/admin-콜봇-월정산-프론트요청.md "화면 명세 1", "요청 계약 1"
  - Shows usage only for the selected month; picking a hospital opens its month (/callbot/settlement/:companyId).
    Rates, cost, charge, margin and per-hospital warnings stay off this screen (revision 5).
  - Top of the page: the compact auto close card (CloseRunSummary) for the list's month - moved back here from the
    default-rate page on 2026-10-27 (user decision 2). It follows the list month and is reloaded by [새로고침].
    Its "not an auto-close target before the first default-rate version" judgement needs the earliest version month,
    so this page reads GET default-plans once (and on refresh); if that fails the card just does not judge it.
  - Source: GET /api/v1/callbot/usage/monthly?month= (DB aggregation only, no paging, no polling).
    The API is getSettlementApi() - with `?settlementMock=1` (not in production builds) a same-shaped mock, and the
    header shows the `목 데이터` badge with a collapsed "DEV 목 조작" area: mark one company deleted (it shows as a
    deleted company in the list and the auto close card - reply table 6; its detail still opens - reply "2026-10-19"),
    auto close on/off, billing indexes not ready (manual run 503 - reply table 11), and the close status of one
    hospital for this month. Status per hospital is also picked from the detail header.
  - State lives in the query: `?month=YYYY-MM&cat=all|hospital|internal&q=<search>&sort=<col>.<asc|desc>`.
    `month` is always in the query - missing or invalid, it is filled with last month (KST) as of opening; otherwise
    back / bookmarks / shared links would open whatever "last month" is later. Defaults for chip, search and sort
    (전체 — the mockup's 병원 default was reverted by user decision 2026-10-27; empty; calls descending) are left out. Changes use router.replace so history does not pile up. Chip, search
    and sort are also kept in sessionStorage `admin.settlementListQuery` (settlementListQuery.js) so the detail's
    `← 병원 목록` and the default-rate page's back link rebuild the same list.
  - Tiles show `hospital_totals` as is (independent of chips and search). Chips and search (company name substring)
    filter on screen; sorting happens inside the chip groups (settlementRules usage list helpers).
  - Switching months fast never lets a late earlier response overwrite a newer one (last request wins, responses
    are kept with their month).
  - Excel export (plan ~/md/plan/콜봇-월정산-정산시작일-엑셀내보내기-계획.md 4-1·4-2): this page owns the selection
    (Set of company_id strings - never in the query). UsageTable shows the select column, the bar on the right of the
    filter row shows 'n곳 선택 · [엑셀 내보내기] · 선택 해제' (disabled button + tooltip when nothing is picked, blocked
    over 100). Rows hidden by a chip or the search stay selected and are announced as '가려진 n곳 포함' in the result
    line. A month change clears the selection and says so; a reload drops ids no longer in the month. The modal
    (SettlementExportModal) gets the selected companies in list order; after an export the selection stays.
    `canExport` (prop, default true - permission hiding comes later) hides the select column and the bar.
  - Settlement start: the result line adds '정산 대상 아님 n곳 포함' when the month has before-start companies
    (`before_start_companies`, missing = 0 - the line is then exactly as before).
-->
<template>
  <div class="container-fluid container-wrap col-sm-12 col-xs-12">
    <div style="margin-top: 28px"></div>

    <!-- Page header -->
    <header class="usage-head">
      <div class="usage-head__text">
        <div class="usage-head__title-row">
          <h1 class="fw-bold page-title">월 정산</h1>
          <!-- Sibling of the title (a block <details> must not sit inside the badge's <span>) -->
          <details v-if="mock" class="mock-dev">
            <summary class="mock-dev__toggle">
              <SettlementMockBadge />
              <span>DEV 목 조작</span>
            </summary>
            <div class="mock-dev__body" role="group" aria-label="목 데이터 조작(개발용)">
              <span class="mock-dev__ctl">
                <span>삭제된 회사</span>
                <a-select
                  class="mock-dev__select"
                  size="small"
                  allow-clear
                  placeholder="없음"
                  :value="mockDeleted"
                  :options="mockCompanyOptions"
                  aria-label="삭제된 회사로 표시(목)"
                  @change="onMockDeleted"
                />
              </span>
              <label class="mock-dev__ctl">
                <a-switch size="small" :checked="mockAutoClose" @change="onMockAutoClose" />
                <span>자동 마감 켜짐</span>
              </label>
              <label class="mock-dev__ctl" :title="MOCK_INDEXES_TITLE">
                <a-switch size="small" :checked="mockIndexesNotReady" @change="onMockIndexes" />
                <span>인덱스 미준비(503)</span>
              </label>
              <span class="mock-dev__ctl">
                <span>{{ monthLabel(month) }} 마감 상태</span>
                <a-select
                  size="small"
                  class="mock-dev__select"
                  :value="mockStatusCompanyId"
                  :options="mockCompanyOptions"
                  show-search
                  option-filter-prop="label"
                  placeholder="병원"
                  aria-label="목 병원"
                  @change="onMockStatusCompany"
                />
                <a-select
                  size="small"
                  class="mock-dev__status"
                  :value="mockStatus"
                  :options="MOCK_STATUS_OPTIONS"
                  :disabled="!mockStatusCompanyId"
                  aria-label="목 마감 상태"
                  @change="onMockStatus"
                />
              </span>
              <span v-if="MOCK_EXPORT_FAULT_OPTIONS.length" class="mock-dev__ctl" :title="MOCK_EXPORT_FAULT_TITLE">
                <span>내보내기 오류</span>
                <a-select
                  size="small"
                  class="mock-dev__status"
                  :value="mockExportFault"
                  :options="MOCK_EXPORT_FAULT_OPTIONS"
                  aria-label="목 다음 내보내기 오류"
                  @change="onMockExportFault"
                />
              </span>
            </div>
          </details>
        </div>
        <p class="usage-head__desc">병원을 누르면 그 병원의 정산서로 들어갑니다.</p>
      </div>

      <div class="usage-head__actions">
        <SettlementMonthPicker :value="month" @change="onMonthChange" />
        <span v-if="generatedAt" class="usage-head__generated" :title="generatedAt.title">
          집계 {{ generatedAt.label }}
        </span>
        <router-link :to="ratesLocation" class="usage-head__rates-link">기본 요율</router-link>
        <a-button :loading="loading" @click="refreshAll">
          <template #icon><ReloadOutlined /></template>
          새로고침
        </a-button>
      </div>
    </header>

    <!-- Selection cleared by a month change - always mounted so screen readers hear it while the table reloads -->
    <p class="usage-sr" aria-live="polite">{{ selectionNotice }}</p>

    <!-- Content -->
    <div class="usage-body">
      <!-- Same environment problem as the list below - one notice is enough -->
      <CloseRunSummary
        v-show="!(shownError && shownError.unimplemented)"
        ref="summaryRef"
        :api="api"
        :month="month"
        :first-plan-month="firstPlanMonth"
        @loaded="onSummaryLoaded"
      />

      <!-- No route on this environment's call-api (404 route missing / 501). The mock hint is dropped in production -->
      <a-alert
        v-if="shownError && shownError.unimplemented"
        type="info"
        show-icon
        class="usage-list__alert"
        :message="SETTLEMENT_UNIMPLEMENTED_TEXT"
      />

      <!-- Any other failure - only when no table is shown yet (a failed refresh keeps the table and only notifies) -->
      <div v-else-if="shownError" class="usage-card">
        <div class="usage-list__error" role="alert">
          <p class="usage-list__error-text">{{ shownError.message }}</p>
          <a-button :loading="loading" @click="load">다시 시도</a-button>
        </div>
      </div>

      <template v-else>
        <UsageTiles :totals="totals" :loading="!current" :month="month" :internal-count="current ? counts.internal : null" />

        <div class="usage-card" :aria-busy="loading ? 'true' : 'false'">
          <a-skeleton v-if="!current" active :title="false" :paragraph="{ rows: 8 }" class="usage-list__skeleton" />

          <a-empty v-else-if="!items.length" class="usage-list__empty" :description="EMPTY_MONTH_TEXT" />

          <template v-else>
            <div class="usage-filter">
              <div class="usage-filter__chips" role="group" aria-label="구분">
                <button
                  v-for="chip in chips"
                  :key="chip.key"
                  type="button"
                  class="usage-filter__chip"
                  :class="{ 'is-active': cat === chip.key }"
                  :aria-pressed="cat === chip.key ? 'true' : 'false'"
                  @click="setCat(chip.key)"
                >
                  {{ chip.label }}
                  <span class="usage-filter__chip-count">{{ chip.count }}</span>
                </button>
              </div>
              <span class="usage-filter__result" aria-live="polite">{{ resultText }}</span>
              <label class="usage-filter__search">
                <span class="usage-filter__search-label">병원 검색</span>
                <a-input
                  v-model:value="searchText"
                  class="usage-filter__search-input"
                  placeholder="병원 이름"
                  allow-clear
                >
                  <template #prefix><SearchOutlined class="usage-filter__search-icon" /></template>
                </a-input>
              </label>

              <!-- Export selection bar (right end of the filter row) -->
              <div v-if="canExport" class="usage-select" role="group" aria-label="엑셀 내보내기 선택">
                <template v-if="selection.count">
                  <span class="usage-select__count">{{ selection.countText }}</span>
                  <span class="usage-select__sep" aria-hidden="true">·</span>
                </template>
                <a-tooltip :title="exportButtonTip">
                  <a-button size="small" :disabled="!selection.canExport" @click="openExport">엑셀 내보내기</a-button>
                </a-tooltip>
                <template v-if="selection.count">
                  <span class="usage-select__sep" aria-hidden="true">·</span>
                  <a-button size="small" type="link" class="usage-select__clear" @click="clearSelection">
                    선택 해제
                  </a-button>
                </template>
                <span class="usage-select__limit" aria-live="polite">{{ selection.limitText }}</span>
              </div>
            </div>

            <a-spin :spinning="refreshing" :delay="200">
              <UsageTable
                :items="visibleItems"
                :month="month"
                :sort="sort"
                :grouped="cat === 'all'"
                :selectable="canExport"
                :selected-ids="selected"
                @sort="onSort"
                @toggle="onToggleSelected"
                @toggle-all="onToggleAllVisible"
              >
                <template #empty>
                  <a-empty :image="SIMPLE_EMPTY_IMAGE" :description="NO_MATCH_TEXT">
                    <div class="usage-list__empty-actions">
                      <a-button @click="resetFilters">전체 보기</a-button>
                    </div>
                  </a-empty>
                </template>
              </UsageTable>
            </a-spin>
          </template>

          <p class="usage-list__footnote">
            통화는 웹 테스트를 뺀 전화 · KST 통화 시각 기준 · 병원별 금액은 병원을 눌러 봅니다
          </p>
        </div>
      </template>
    </div>

    <SettlementExportModal
      v-if="canExport"
      :open="exportOpen"
      :month="month"
      :companies="exportCompanies"
      :api="api"
      @close="onExportClose"
      @done="onExportDone"
    />
  </div>
</template>

<script>
export default {
  name: 'SettlementList',
};
</script>

<script setup>
import { ReloadOutlined, SearchOutlined } from '@ant-design/icons-vue';
import { Empty, message } from 'ant-design-vue';
import { computed, nextTick, onBeforeUnmount, ref, shallowRef, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import CloseRunSummary from './components/CloseRunSummary.vue';
import SettlementExportModal from './components/SettlementExportModal.vue';
import SettlementMockBadge from './components/SettlementMockBadge.vue';
import SettlementMonthPicker from './components/SettlementMonthPicker.vue';
import UsageTable from './components/UsageTable.vue';
import UsageTiles from './components/UsageTiles.vue';
import {
  compareMonth,
  formatNumber,
  formatKstDate,
  formatKstDateTime,
  formatKstTime,
  isNumber,
  isValidMonth,
  monthLabel,
  resolveMonthParam,
} from './service/settlementFormat';
import {
  EMPTY_SELECTION_TIP,
  exportCompaniesOf,
  pruneSelection,
  selectionAfterMonthChange,
  selectionSummary,
  setVisibleSelected,
  toggleSelected,
} from './service/settlementExportSelection';
import { writeSavedListQuery } from './service/settlementListQuery';
import {
  SETTLEMENT_UNIMPLEMENTED_TEXT,
  getSettlementApi,
  isSettlementMockEnabled,
  settlementMockControls,
} from './service/settlementMock';
import {
  CATEGORY_LABELS,
  DEFAULT_USAGE_CAT,
  SETTLEMENT_ROUTE_NAMES,
  filterUsageItems,
  formatUsageSort,
  normalizeUsageCat,
  parseUsageSort,
  serializeListQuery,
  sortUsageItems,
  toggleUsageSort,
  usageCounts,
} from './service/settlementRules';
import { isCallApiUnimplemented, parseCallApiError } from '@/views/Callbot/service/callApiError';

const LOAD_FAIL_TEXT = '병원 사용량을 불러오지 못했습니다.';
const EMPTY_MONTH_TEXT = '이 달에 콜봇 사용량이 있는 병원이 없습니다.';
const NO_MATCH_TEXT = '검색 결과가 없습니다.';
/** 표 안 빈 결과는 작은 그림으로 */
const SIMPLE_EMPTY_IMAGE = Empty.PRESENTED_IMAGE_SIMPLE;
/** 검색어를 주소에 옮기기까지 — 글자마다 history.replaceState 를 부르지 않게(Safari 는 횟수 제한이 있다) */
const SEARCH_WRITE_DELAY_MS = 250;

const LIST_ROUTE = SETTLEMENT_ROUTE_NAMES.list;

const props = defineProps({
  /** Export selection column, bar and modal. The route passes nothing yet (true) - permission hiding comes later */
  canExport: { type: Boolean, default: true },
});

const route = useRoute();
const router = useRouter();

// 목 스위치와 API 는 페이지를 열 때 한 번 정한다
const mock = isSettlementMockEnabled();
const api = getSettlementApi();

/** @param {unknown} v 쿼리 값(같은 키를 여러 번 받으면 배열) */
const firstString = (v) => {
  const s = Array.isArray(v) ? v[0] : v;
  return typeof s === 'string' ? s : '';
};

// ---------------------------------------------------------------------------
// 쿼리 상태 — 목록 라우트에 있는 동안의 쿼리만 쓴다. 상세로 떠나는 순간(이 화면이 내려가기 전)
// 상세 쿼리로 달·구분·정렬을 다시 읽어 불필요한 재조회나 sessionStorage 덮어쓰기가 일어나지 않게.
// ---------------------------------------------------------------------------
const listQuery = shallowRef({ ...route.query });
watch(
  () => route.query,
  (query) => {
    if (route.name === LIST_ROUTE) listQuery.value = query;
  }
);

// '지난 달' 기본값은 연 시점 기준으로 고정한다 — 월 선택의 이번 달/지난 달 버튼과 같은 기준
const openedAt = Date.now();

const month = computed(() => resolveMonthParam(listQuery.value.month, openedAt));
const cat = computed(() => normalizeUsageCat(listQuery.value.cat));
const sort = computed(() => parseUsageSort(listQuery.value.sort));

/** 쿼리 일부를 바꾼다(빈 값은 뺀다). 다른 키(목 스위치 등)는 그대로 둔다 */
function replaceQuery(patch) {
  if (route.name !== LIST_ROUTE) return;
  const next = { ...route.query };
  for (const [key, value] of Object.entries(patch)) {
    if (value === undefined || value === null || value === '') delete next[key];
    else next[key] = value;
  }
  const keys = Object.keys(next);
  const same =
    keys.length === Object.keys(route.query).length && keys.every((k) => String(next[k]) === String(route.query[k]));
  if (!same) void router.replace({ query: next });
}

// `month` 는 늘 주소에 둔다 — 첫 진입·사이드바로 다시 들어오기처럼 없거나 틀리면 지금 보이는 달(연 시점의
// 지난 달)을 넣는다. 보이는 달은 그대로라 다시 받지 않고, replace 라 이력도 쌓이지 않는다.
watch(
  () => listQuery.value.month,
  (raw) => {
    if (!isValidMonth(firstString(raw))) replaceQuery({ month: month.value });
  },
  { immediate: true }
);

function onMonthChange(value) {
  replaceQuery({ month: value });
}

function setCat(value) {
  replaceQuery({ cat: value === DEFAULT_USAGE_CAT ? undefined : value });
}

function onSort(key) {
  replaceQuery({ sort: formatUsageSort(toggleUsageSort(sort.value, key)) });
}

// ---------------------------------------------------------------------------
// 검색 — 입력칸이 곧바로 거르고, 주소(q)에는 잠시 뒤 옮긴다
// ---------------------------------------------------------------------------
const searchText = ref(firstString(listQuery.value.q));
let searchTimer = null;

function cancelSearchWrite() {
  if (searchTimer) clearTimeout(searchTimer);
  searchTimer = null;
}

watch(searchText, (text) => {
  cancelSearchWrite();
  searchTimer = setTimeout(() => {
    searchTimer = null;
    replaceQuery({ q: (text ?? '').trim() || undefined });
  }, SEARCH_WRITE_DELAY_MS);
});

// 바깥에서 바뀐 q(사이드바 메뉴로 같은 화면에 다시 들어옴 등)만 입력칸에 옮긴다. 옮기기를 기다리는
// 동안은 입력 중인 값이 이긴다. 끝 공백은 주소에 없으므로 비교는 trim 으로 한다.
watch(
  () => listQuery.value.q,
  (raw) => {
    if (searchTimer) return;
    const routeQ = firstString(raw).trim();
    if (routeQ !== (searchText.value ?? '').trim()) searchText.value = routeQ;
  }
);

/** 검색 결과 없음 → '전체 보기': 구분을 전체로 두고 검색을 푼다(정렬은 둔다) */
function resetFilters() {
  cancelSearchWrite();
  searchText.value = '';
  replaceQuery({ cat: undefined, q: undefined });
}

onBeforeUnmount(cancelSearchWrite);

// 상세의 `← 병원 목록`·기본 요율 화면의 돌아가기 링크가 읽는다(기본값은 빠진 JSON). 저장하지 못해도
// 목록은 돈다 — 돌아가기 링크가 기본 필터로 열릴 뿐이다
watch(
  () => serializeListQuery({ cat: cat.value, q: searchText.value, sort: sort.value }),
  (json) => void writeSavedListQuery(json),
  { immediate: true }
);

// ---------------------------------------------------------------------------
// 불러오기 — 응답은 그 달과 같이 둔다. 마지막 요청만 반영한다.
// ---------------------------------------------------------------------------
/** @type {import('vue').ShallowRef<{ month: string, body: object }|null>} */
const loaded = shallowRef(null);
/** @type {import('vue').ShallowRef<{ month: string, unimplemented: boolean, message: string }|null>} */
const loadError = shallowRef(null);
const loading = ref(false);
let loadSeq = 0;

async function load() {
  const seq = ++loadSeq;
  const target = month.value;
  loading.value = true;
  try {
    const body = await api.listMonthlyUsage(target);
    if (seq !== loadSeq) return;
    loaded.value = { month: target, body: body && typeof body === 'object' ? body : {} };
    loadError.value = null;
  } catch (err) {
    if (seq !== loadSeq) return;
    if (isCallApiUnimplemented(err)) {
      loaded.value = null;
      loadError.value = { month: target, unimplemented: true, message: SETTLEMENT_UNIMPLEMENTED_TEXT };
    } else if (loaded.value && loaded.value.month === target) {
      // 새로고침 실패 — 보이던 표는 그대로 두고 알리기만 한다
      message.error(parseCallApiError(err, LOAD_FAIL_TEXT));
    } else {
      loadError.value = { month: target, unimplemented: false, message: parseCallApiError(err, LOAD_FAIL_TEXT) };
    }
  } finally {
    if (seq === loadSeq) loading.value = false;
  }
}

// 첫 진입과 달 바꿈마다(구분·검색·정렬은 화면에서 거르므로 다시 받지 않는다)
watch(month, () => void load(), { immediate: true });

/** 지금 달의 응답(다른 달 응답은 보이지 않는다) */
const current = computed(() => (loaded.value && loaded.value.month === month.value ? loaded.value.body : null));
const shownError = computed(() =>
  loadError.value && loadError.value.month === month.value ? loadError.value : null
);
/** 표가 보이는 채로 다시 받는 중 */
const refreshing = computed(() => loading.value && current.value !== null);

const totals = computed(() => current.value?.hospital_totals ?? null);

const items = computed(() => {
  const list = current.value?.items;
  return Array.isArray(list) ? list.filter((it) => it && it.company_id) : [];
});

const counts = computed(() => usageCounts(items.value));

// ---------------------------------------------------------------------------
// Mock only (DEV 목 조작) - deleted company (shows as deleted in the list and the auto close card; the detail and
// other company routes still open), auto close on/off, indexes not ready, one hospital's close status this month.
// Every change reloads what it affects. Mock state is not reactive, so mockTick forces a re-read.
// ---------------------------------------------------------------------------
const MOCK_STATUS_OPTIONS = mock ? settlementMockControls.STATUS_OPTIONS.map((option) => ({ ...option })) : [];
const MOCK_EXPORT_FAULT_OPTIONS = mock
  ? (settlementMockControls.EXPORT_FAULT_OPTIONS ?? []).map((option) => ({ ...option }))
  : [];
const MOCK_EXPORT_FAULT_TITLE = '다음 [내보내기] 한 번만 이 오류로 실패합니다(한 번 쓰이면 없음으로 돌아갑니다).';
const MOCK_INDEXES_TITLE =
  '켜면 [지금 마감 실행]·[지금 다시 실행]이 503 BILLING_INDEXES_NOT_READY 로 실패합니다(배포 직후 인덱스가 아직 없는 환경 흉내).';

const readMockDeleted = () => (mock ? settlementMockControls.getDeletedCompanies()[0] : undefined);
const mockDeleted = ref(readMockDeleted());
const mockAutoClose = ref(mock ? settlementMockControls.getAutoCloseEnabled() : true);
const mockIndexesNotReady = ref(mock ? settlementMockControls.getIndexesNotReady() : false);
const mockStatusCompanyId = ref(null);
const mockTick = ref(0);

const mockCompanyOptions = computed(() =>
  items.value
    .map((it) => ({ value: String(it.company_id), label: it.company_name || String(it.company_id) }))
    .sort((a, b) => a.label.localeCompare(b.label, 'ko'))
);

// Keep a valid hospital picked for the status control when the month's rows change
watch(mockCompanyOptions, (options) => {
  if (!mock) return;
  if (!options.some((o) => o.value === mockStatusCompanyId.value)) mockStatusCompanyId.value = options[0]?.value ?? null;
});

const mockStatus = computed(() => {
  void mockTick.value;
  if (!mock || !mockStatusCompanyId.value) return undefined;
  return settlementMockControls.getStatus(mockStatusCompanyId.value, month.value);
});

function reloadSummary() {
  void summaryRef.value?.reload();
}

/** Only one company is marked deleted (clearing restores it) */
function onMockDeleted(value) {
  if (!mock) return;
  for (const id of settlementMockControls.getDeletedCompanies()) settlementMockControls.setCompanyDeleted(id, false);
  if (value) settlementMockControls.setCompanyDeleted(value, true);
  mockDeleted.value = readMockDeleted();
  void load();
  reloadSummary();
}

function onMockAutoClose(checked) {
  if (!mock) return;
  settlementMockControls.setAutoCloseEnabled(!!checked);
  mockAutoClose.value = settlementMockControls.getAutoCloseEnabled();
  reloadSummary();
}

function onMockIndexes(checked) {
  if (!mock) return;
  settlementMockControls.setIndexesNotReady(!!checked);
  mockIndexesNotReady.value = settlementMockControls.getIndexesNotReady();
}

const mockExportFault = computed(() => {
  void mockTick.value;
  if (!mock || typeof settlementMockControls.getExportFault !== 'function') return undefined;
  return settlementMockControls.getExportFault();
});

function onMockExportFault(value) {
  if (!mock || typeof settlementMockControls.setExportFault !== 'function') return;
  settlementMockControls.setExportFault(value ?? 'none');
  mockTick.value += 1;
}

function onMockStatusCompany(value) {
  mockStatusCompanyId.value = value ?? null;
}

function onMockStatus(value) {
  if (!mock || !mockStatusCompanyId.value || !value) return;
  settlementMockControls.setStatus(mockStatusCompanyId.value, month.value, value);
  mockTick.value += 1;
  reloadSummary();
}

const chips = computed(() => [
  { key: 'all', label: '전체', count: counts.value.all },
  { key: 'hospital', label: CATEGORY_LABELS.hospital, count: counts.value.hospital },
  { key: 'internal', label: CATEGORY_LABELS.internal, count: counts.value.internal },
]);

const visibleItems = computed(() =>
  sortUsageItems(filterUsageItems(items.value, { cat: cat.value, q: searchText.value }), sort.value)
);

const searchResultText = computed(() => {
  const q = (searchText.value ?? '').trim();
  return q ? `‘${q}’ 검색 결과 ${visibleItems.value.length}곳` : '';
});

/** Before-start companies of the month (missing on an older call-api = 0) */
const beforeStartCount = computed(() => {
  const n = current.value?.before_start_companies;
  return isNumber(n) && n > 0 ? n : 0;
});

/**
 * The live result line - search result, hidden selected rows, before-start companies. With none of them it is the
 * same empty string as before
 */
const resultText = computed(() =>
  [
    searchResultText.value,
    props.canExport ? selection.value.hiddenText : '',
    beforeStartCount.value ? `정산 대상 아님 ${formatNumber(beforeStartCount.value)}곳 포함` : '',
  ]
    .filter(Boolean)
    .join(' · ')
);

// ---------------------------------------------------------------------------
// Export selection - Set<company_id string>, kept on this page only (never in the query). The helpers always return
// a new Set, so replacing the shallowRef is what triggers the re-render.
// ---------------------------------------------------------------------------
const selected = shallowRef(new Set());
/** Screen-reader notice (month change cleared the selection) */
const selectionNotice = ref('');
const exportOpen = ref(false);

const visibleIds = computed(() => visibleItems.value.map((it) => String(it.company_id)));
const selection = computed(() => selectionSummary(selected.value, visibleIds.value));
const exportButtonTip = computed(() => (selection.value.count ? selection.value.limitText || undefined : EMPTY_SELECTION_TIP));
/** Companies handed to the modal - list order */
const exportCompanies = computed(() => exportCompaniesOf(selected.value, items.value));

function onToggleSelected(companyId, checked) {
  selected.value = toggleSelected(selected.value, String(companyId), !!checked);
}

/** Header box - the visible rows only (rows hidden by a chip or the search keep their state) */
function onToggleAllVisible(checked) {
  selected.value = setVisibleSelected(selected.value, visibleIds.value, !!checked);
}

function clearSelection() {
  selected.value = new Set();
}

function openExport() {
  if (!selection.value.canExport) return;
  exportOpen.value = true;
}

function onExportClose() {
  exportOpen.value = false;
  // The mock export fault is one-shot - show what is left of it
  mockTick.value += 1;
}

/** The selection stays, so the same companies can be exported again (e.g. with other contents) */
function onExportDone() {
  mockTick.value += 1;
}

// A month change clears the selection (another month's companies are other rows) and says so when it was not empty
watch(month, () => {
  exportOpen.value = false;
  const { selected: next, notice } = selectionAfterMonthChange(selected.value);
  if (!notice) return;
  selected.value = next;
  // Empty first, then the text after the DOM update - the same text is announced again on a second month change
  selectionNotice.value = '';
  void nextTick(() => {
    selectionNotice.value = notice;
  });
  message.info({ content: notice, key: 'settlement-selection-cleared' });
});

// A reload of the month drops ids that are no longer in it (only when something would actually go)
watch(items, (list) => {
  if (!selected.value.size || !current.value) return;
  const next = pruneSelection(selected.value, list);
  if (next.size !== selected.value.size) selected.value = next;
});

/** 집계 시각 — 오늘이면 시:분만 */
const generatedAt = computed(() => {
  const at = current.value?.generated_at;
  if (!isNumber(at)) return null;
  const today = formatKstDate(at) === formatKstDate(Date.now());
  return {
    label: today ? formatKstTime(at) : formatKstDateTime(at),
    title: `집계 시각 ${formatKstDateTime(at)}(KST)`,
  };
});

/** Default-rate page - the month goes along so its back link returns to this month */
const ratesLocation = computed(() => ({
  name: SETTLEMENT_ROUTE_NAMES.rates,
  query: { month: month.value },
}));

// ---------------------------------------------------------------------------
// Auto close card - follows the list month. It needs the earliest default-rate version month to say that earlier
// months are not auto-close targets; read once (and on refresh). A failure leaves '' (the card does not judge it).
// ---------------------------------------------------------------------------
const summaryRef = ref(null);
const firstPlanMonth = ref('');
let plansSeq = 0;

async function loadFirstPlanMonth() {
  const my = ++plansSeq;
  try {
    const res = await api.listDefaultPlans();
    if (my !== plansSeq) return;
    const months = (Array.isArray(res?.items) ? res.items : [])
      .map((p) => p?.effective_from)
      .filter(isValidMonth)
      .sort(compareMonth);
    firstPlanMonth.value = months[0] ?? '';
  } catch {
    // Optional context for the card only - the list and the card work without it
    if (my === plansSeq) firstPlanMonth.value = '';
  }
}

void loadFirstPlanMonth();

function onSummaryLoaded() {
  mockTick.value += 1;
}

/** [새로고침] - the list, the auto close card and the version month */
function refreshAll() {
  void load();
  reloadSummary();
  void loadFirstPlanMonth();
}
</script>

<style scoped lang="scss">
@use './styles/settlement' as *;

.container-wrap {
  padding: 0 16px;
}

.page-title {
  color: #000;
  font-size: 1.8rem;
  margin-bottom: 0;
}

// ---- header: title + one line on the left, month / time / links on the right ----
.usage-head {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px 16px;
  margin-bottom: 20px;

  &__text {
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 0;
  }

  &__title-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px 12px;
  }

  &__desc {
    margin: 0;
    font-size: 14px;
    color: $st-text-secondary;
  }

  &__actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px 10px;
  }

  &__generated {
    @include st-num;
    font-size: 12.5px;
    color: $st-muted;
    cursor: help;
  }

  // Looks like the neighbouring default button (it is a link to another page)
  &__rates-link {
    display: inline-flex;
    align-items: center;
    height: 32px;
    padding: 0 14px;
    border: 1px solid $st-border-strong;
    border-radius: 6px;
    background: #fff;
    color: $st-text;
    font-size: 14px;
    font-weight: 500;
    text-decoration: none;

    &:hover {
      border-color: $st-accent;
      color: $st-accent;
    }

    &:focus-visible {
      outline: 2px solid $st-accent;
      outline-offset: 2px;
    }
  }
}

// ---- DEV mock controls (collapsed; same look as the detail header's) ----
.mock-dev {
  font-size: 12px;

  &__toggle {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-weight: 600;
    color: $st-warning;
    cursor: pointer;
  }

  &__body {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px 12px;
    max-width: 720px;
    margin-top: 6px;
    padding: 6px 10px;
    border: 1px dashed $st-warning-border;
    border-radius: 8px;
    background: #fffdf8;
    color: $st-text-secondary;
  }

  &__ctl {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  &__select {
    width: 180px;
  }

  &__status {
    width: 150px;
  }
}

// Screen-reader only live region
.usage-sr {
  @include st-sr-only;
}

// ---- content ----
.usage-body {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-bottom: 8px;
}

.usage-card {
  overflow: hidden;
  border: 1px solid $st-border;
  border-radius: 12px;
  background: #fff;
}

.usage-list {
  &__alert {
    margin: 0;
  }

  // The global ant-design.scss makes .ant-btn flex, so text-align alone does not center it
  &__error {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 32px 16px;
    text-align: center;
  }

  &__error-text {
    margin: 0;
    font-size: 13px;
    color: $st-danger;
  }

  &__empty-actions {
    display: flex;
    justify-content: center;
  }

  &__skeleton {
    padding: 16px;
  }

  &__empty {
    margin: 32px 0;
  }

  &__footnote {
    margin: 0;
    padding: 12px 16px;
    border-top: 1px solid $st-border;
    font-size: 12.5px;
    color: $st-muted;
  }
}

// ---- chips + search ----
.usage-filter {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px 12px;
  padding: 14px 16px;
  border-bottom: 1px solid $st-border;

  &__chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }

  &__chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 32px;
    padding: 0 12px;
    border: 1px solid $st-border-strong;
    border-radius: 8px;
    background: #fff;
    color: $st-text-secondary;
    font-size: 13.5px;
    font-weight: 500;
    cursor: pointer;

    &:hover {
      color: $st-text;
    }

    &:focus-visible {
      outline: 2px solid $st-accent;
      outline-offset: 1px;
    }

    &.is-active {
      border-color: #1b1f24;
      background: #1b1f24;
      color: #fff;
      font-weight: 600;
    }
  }

  &__chip-count {
    @include st-num;
  }

  &__result {
    font-size: 12px;
    color: $st-muted;
  }

  &__search {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin: 0 0 0 auto;
    font-size: 13px;
    color: $st-text-secondary;
  }

  &__search-label {
    white-space: nowrap;
  }

  &__search-input {
    width: 220px;
  }

  &__search-icon {
    color: $st-muted;
  }
}

// ---- export selection bar (after the search, right end of the filter row) ----
.usage-select {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 8px;
  font-size: 13px;
  color: $st-text-secondary;

  &__count {
    @include st-num;
    color: $st-text;
    font-weight: 600;
  }

  &__sep {
    color: $st-muted;
  }

  // Undo the global `button { height: 40px }` and the link button's side padding so it reads as text
  &__clear {
    height: auto;
    padding: 0 2px;
  }

  // Over the limit only. Kept mounted (aria-live) - while empty it takes no room but stays in the accessibility tree
  &__limit {
    flex-basis: 100%;
    color: $st-danger;
    font-size: 12px;
    text-align: right;

    &:empty {
      @include st-sr-only;
    }
  }
}
</style>
